import React from "react";
import { X, Loader2, Search, ExternalLink, ChevronUp } from "lucide-react";

/**
 * ImageSearchResultsPanel — extracted from ChatsPage.jsx (iter 156.2, Feb 2026).
 *
 * Shows the result of "Image-to-Product Search" (an agent uploads or
 * pastes a customer's photo → Claude Vision identifies the product, then
 * we substring-match against our catalog and surface top 3 candidates).
 *
 * Always rendered above the composer when either:
 *   - `searching` is true (spinner)
 *   - `results` is non-null (chips + product cards)
 */
export const ImageSearchResultsPanel = ({
  searching,
  results,
  onClose,
  onInsertProduct,
}) => {
  if (!searching && !results) return null;
  return (
    <div className="border-t border-[--border] bg-purple-50 px-4 py-2.5 max-h-[320px] overflow-y-auto" data-testid="image-search-results">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-semibold text-purple-700 uppercase tracking-wider flex items-center gap-1">
          <Search size={11} /> Image-to-Product Search
        </span>
        <button onClick={onClose} className="text-purple-400 hover:text-purple-600"><X size={14} /></button>
      </div>
      {searching ? (
        <div className="flex items-center gap-2 text-xs text-purple-600">
          <Loader2 size={14} className="animate-spin" /> Analyzing image & searching catalog...
        </div>
      ) : results ? (
        <div className="space-y-2">
          {/* Detected product chips */}
          <div className="flex flex-wrap gap-1.5 text-[10px]">
            {results.product_type && (
              <span className="px-1.5 py-0.5 bg-white border border-purple-200 rounded font-semibold text-purple-700">{results.product_type}</span>
            )}
            {results.material && (
              <span className="px-1.5 py-0.5 bg-white border border-purple-200 rounded text-purple-700">{results.material}</span>
            )}
            {results.search_query && (
              <span className="px-1.5 py-0.5 bg-white border border-purple-200 rounded italic text-purple-700">"{results.search_query}"</span>
            )}
          </div>
          {/* Top matches as cards with thumbnails */}
          {results.matches?.length > 0 && (
            <>
              <p className="text-[9px] text-purple-600 font-bold uppercase tracking-wider">Top {Math.min(results.matches.length, 3)} candidates from catalog</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {results.matches.slice(0, 3).map((m, i) => (
                  <div key={`${m.url}-${i}`} className="bg-white rounded-lg border border-purple-200 p-2 flex flex-col gap-1.5" data-testid={`image-search-card-${i}`}>
                    <a href={m.url} target="_blank" rel="noopener noreferrer" className="block">
                      {m.thumbnail ? (
                        <img
                          src={m.thumbnail}
                          alt={m.title || "product"}
                          loading="lazy"
                          className="w-full h-32 object-cover rounded border border-purple-100 bg-gray-50"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-full h-32 rounded border border-purple-100 bg-gray-50 flex items-center justify-center">
                          <Search size={20} className="text-gray-300" />
                        </div>
                      )}
                    </a>
                    <p className="text-[10px] text-purple-900 leading-tight line-clamp-2 font-medium" title={m.title}>{m.title || m.url.split("/").pop()}</p>
                    <div className="flex items-center justify-between text-[9px] text-purple-500">
                      {m.itemcode && <span className="font-mono">{m.itemcode}</span>}
                      {(m.inr_price || m.usd_price) && (
                        <span className="font-semibold text-emerald-700">
                          {m.inr_price ? `₹${m.inr_price}` : `$${m.usd_price}`}
                        </span>
                      )}
                    </div>
                    <button
                      data-testid={`image-search-insert-${i}`}
                      onClick={() => onInsertProduct(m)}
                      className="w-full text-[10px] font-bold bg-purple-600 hover:bg-purple-700 text-white rounded py-1 transition flex items-center justify-center gap-1"
                    >
                      <ChevronUp size={10} /> Insert link
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {(!results.matches || results.matches.length === 0) && (
            <p className="text-[10px] text-purple-600 italic">No catalog matches — try the reverse-image search below.</p>
          )}
          {/* Fallback: external search tools */}
          <div className="flex flex-wrap gap-1.5 pt-1 border-t border-purple-200">
            {results.google_lens_url && (
              <a href={results.google_lens_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] bg-white rounded px-2 py-1 hover:bg-purple-100 transition border border-purple-200 text-purple-700" data-testid="google-lens-link">
                <Search size={10} /> Google Lens
                <ExternalLink size={9} className="text-purple-400" />
              </a>
            )}
            {results.google_image_search_url && (
              <a href={results.google_image_search_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] bg-white rounded px-2 py-1 hover:bg-purple-100 transition border border-purple-200 text-purple-700">
                <Search size={10} /> Google "{results.search_query}"
                <ExternalLink size={9} className="text-purple-400" />
              </a>
            )}
          </div>
        </div>
      ) : (
        <p className="text-xs text-purple-600">No results</p>
      )}
    </div>
  );
};
